import React, { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { useParseRouteActions, useFetchData, getDropdownSemanticOptionsFromData, downloadFile } from "../../../helpers/GlobalHelper";
import LmsTable from "../ui/LmsTable";
import { AddProformaForm } from "../ui/LmsSharedForms";
import { FeatureFlags } from "../../constants/LocalServerConstants";
import { guid } from "../../../Helpers";

const DOCUMENT_MASTER_OPTIONS = {
  APPRAISAL: "84e1e7dd-aab0-44f7-bedd-a6440137fee1",
  PURCHASE_SALES_OPTIONS: "3eb0049d-631f-4ddf-8a9d-a65200caf43b"
};

const DISPLAY_ACTION_FIELD_RULES = [
  DOCUMENT_MASTER_OPTIONS.APPRAISAL,
  DOCUMENT_MASTER_OPTIONS.PURCHASE_SALES_OPTIONS
];

const addDocumentFields = (myForm: BCFormController) => {
  const isActionHiddenFunc = ({ inputs }) => {
    return !DISPLAY_ACTION_FIELD_RULES.includes(inputs?.documentMasterId);
  };

  myForm.addCenteredGroup([myForm.getDropdownField("Document Master", "metadata/doc-masters", { required: true })]);
  myForm.addCenteredGroup([myForm.getDisplayField("Document Category")], {
    isHiddenFunc: ({ inputs }) => !inputs?.documentMasterId
  });

  myForm.addCenteredGroup([
    myForm.getDropdownField("Organization", "metadata/list-borrower-organizations", {
      name: "organizationId",
      clearable: true,
    }),
  ]);

  myForm.addCenteredGroup([myForm.getDropdownField("Application/Loan #", "document/list-borrower-loans", {
    name: "applicationLoanIds",
    dropdownMultiple: true,
    dependencyField:"organizationId",
  })]);

  myForm.addCenteredGroup([
    myForm.getLabelField("Link Option", { width: 2, wrapLabel: true }),
    myForm.getGroupRadioField("Link Properties"),
    myForm.getGroupRadioField("Link Draw", { label: "Link Draws" }),
  ], { radioGroupName: "linkOption" });
  myForm.addCenteredGroup([myForm.getDropdownField("Property Address", [], { name: "loanPropertyIds", dropdownMultiple: true })], {
    isHidden: { linkOption: "Link Properties" },
    isHiddenOperation: "notequal"
  });
  myForm.addCenteredGroup([myForm.getDropdownField("Draw #", [], { name: "loanDrawIds", dropdownMultiple: true })], {
    isHidden: { linkOption: "Link Draw" },
    isHiddenOperation: "notequal"
  });
  myForm.addCenteredGroup([
    myForm.getCalendarField("Effective Date", { isDefaultDateNow: true, required: true }),
    myForm.getCalendarField("Expiration Date", { name: "expiryDate", required: true })
  ]);
  myForm.addCenteredGroup([myForm.getFileField("Document", {
    name: "documentFile",
    documentFileName: "documentFileName",
    fileFieldName: "document",
    documentDownloadSuffix: "document/download/{id}",
    required: true
  })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Archive"), myForm.getCheckboxField("Include in LRC", { name: "includeInLrcpolling" })]);
  myForm.addCenteredGroup([
    myForm.getLabelField("Action", { width: 1 }),
    myForm.getGroupRadioField("Add New Document"),
    myForm.getGroupRadioField("Replace Existing Document"),
  ], { radioGroupName: "action", radioGroupValue: "Replace Existing Document", isHiddenFunc: isActionHiddenFunc });
  myForm.addCenteredGroup([myForm.getCheckboxField("Archive Existing Document")], { isHidden: { action: "Replace Existing Document" } });
  myForm.addCenteredGroup([myForm.getTextareaField("Description")]);
  return myForm;
};

const getApplicationAndPropertiesOptions = async (options, fetchData) => {
  if (options.setAllDropdownOptionsData) {
    const { setIsFormLoading } = options;
    setIsFormLoading && setIsFormLoading(true);
    const drawsOptions = await fetchData("draws/list-draws-by-loanids", {
      fetchMethod: "POST",
      data: options.inputs.applicationLoanIds,
    });

    const propertiesOptions = await fetchData("loanproperty/list-properties-by-loanids", {
      fetchMethod: "POST",
      data: options.inputs.applicationLoanIds,
    });

    setIsFormLoading && setIsFormLoading(false);
    const newDropdownOptions = {} as any;
    if (drawsOptions?.data) {
      newDropdownOptions.loanDrawIds = getDropdownSemanticOptionsFromData(drawsOptions?.data);
    }

    if (propertiesOptions?.data) {
      newDropdownOptions.loanPropertyIds = getDropdownSemanticOptionsFromData(
        propertiesOptions?.data
      );
    }

    options.setAllDropdownOptionsData((prevOptions) => ({ ...prevOptions, ...newDropdownOptions }));
  }
};

const onDocumentFormChanged = async (options, fetchData) => {
  const setEmptyDrawPropertyOptions = () => {
    options.setAllDropdownOptionsData((prevDropdownOpts) => ({
      ...prevDropdownOpts,
      loanDrawIds: [],
      loanPropertyIds: [],
    }));
  };

  switch (options.lastUpdated) {
    case "documentMasterId": {
      const {
        inputs: { documentMasterId, includeInLrcpolling, expiryDate },
        setIsFormLoading,
        allDropdownOptionsData,
      } = options;

      setIsFormLoading(true);
      const docDetails = await fetchData(`/document/document-master/${documentMasterId}`);
      setIsFormLoading(false);
      let newExpiryDate = expiryDate;
      let newIncludeInLrcpolling = includeInLrcpolling;
      if (docDetails.data) {
        const d = new Date();
        d.setDate(d.getDate() + docDetails.data.expiryDays);
        newExpiryDate = d.toISOString();
        newIncludeInLrcpolling = docDetails.data.includeInLrcpolling;
      }

      const documentMasterOption = allDropdownOptionsData["metadata/doc-masters"].find(
        (docOption) => docOption.id === documentMasterId
      );

      options.setInputs((prevInputs) => ({
        ...prevInputs,
        expiryDate: newExpiryDate,
        includeInLrcpolling: newIncludeInLrcpolling,
        documentCategory: documentMasterOption ? documentMasterOption.documentCategoryName : "",
      }));

      break;
    }
    case "applicationLoanIds": {
      setEmptyDrawPropertyOptions();
      const {
        inputs: { applicationLoanIds },
      } = options;
      if (applicationLoanIds.length > 0) {
        await getApplicationAndPropertiesOptions(options, fetchData);
      }

      break;
    }
    case "organizationId": {
      options.setInputs((prevInputs) => ({
        ...prevInputs,
        applicationLoanIds: [],
        loanDrawIds: [],
        loanPropertyIds: [],
      }));

      break;
    }
  }
};

const AddDocumentForm = ({ loanDetailsId, loanId = "", nwccmessageId = "" }) => {
  const initialDataSuffix = loanDetailsId
    ? `loandetails/${loanDetailsId}`
    : nwccmessageId
    ? `/loandetails/document-logs/${nwccmessageId}/initial-data`
    : "";

  const [{ data, isLoading, error, fetchData }] = useFetchData(initialDataSuffix);

  const initialData = useMemo(() => {
    const defaultData = {
      includeInLrcpolling: true,
      applicationLoanIds: loanId ? [loanId] : [],
    };

    if (data) {
      const apiData = { ...data, id: guid() };
      ["documentMasterId", "expiryDate", "effectiveDate"].forEach(
        (prop) => delete apiData[prop]
      );

      if (Array.isArray(apiData.loanIds)) {
        apiData.applicationLoanIds = apiData.loanIds;
      } else if (apiData.loanId) {
        apiData.applicationLoanIds = [apiData.loanId];
      }

      return {
        ...defaultData,
        ...apiData,
        organizationId: data.organizationId || data.borrowerOrganizationId || "",
      };
    }

    return defaultData;
  }, [data]);

  const formatData = (formData) => {
    const payload = { ...formData, ...(nwccmessageId && { nwccmessageId: nwccmessageId }) };
    for (const key in payload) {
      if (key === "linkOption") {
        if (payload[key] === "Link Properties" && payload?.loanDrawIds) {
          delete payload.loanDrawIds;
        } else if (payload[key] === "Link Draw" && payload?.loanPropertyIds) {
          delete payload.loanPropertyIds;
        }
      }

      if (Array.isArray(payload[key]) && payload[key].length === 0) {
        delete payload[key];
      }
    }

    return payload;
  };

  const myForm = addDocumentFields(
    new BCFormController(initialData, {
      submitSuffix: nwccmessageId ? "loandetails/nwccmessage-logs" : "document",
      submitMethod: "POST",
      payloadDataType: "FormData",
      dataFormatterFunc: formatData,
      onDropdownOptionsLoaded: (loadedDropdownOpts, options) => {
        const { loanPropertyIds, loanDrawIds } = loadedDropdownOpts;
        const {
          inputs: { applicationLoanIds = [] },
        } = options;

        if (
          applicationLoanIds.length > 0 &&
          loanPropertyIds.length === 0 &&
          loanDrawIds.length === 0
        ) {
          onDocumentFormChanged({ ...options, lastUpdated: "applicationLoanIds" }, fetchData);
        }
      },
    })
  );

  return (
    <LmsFormGenerator
      myForm={myForm}
      actions={["save-new", "save-close", "close"]}
      isLoading={isLoading}
      error={error}
      onFormChanged={(options) => onDocumentFormChanged(options, fetchData)}
    />
  );
};

const EditDocumentForm = ({ documentId }) => {
  const [{ data, isLoading, error, fetchData }] = useFetchData(`document/logs/${documentId}`);
  const myForm = useMemo(() => {
    return addDocumentFields(new BCFormController(data, {
      submitSuffix: "document",
      submitMethod: "PUT",
      clearSuffixesOnSubmit: [`document/logs/${documentId}`],
      deleteSuffix: "document",
      payloadDataType: "FormData",
      spreadFormDataArrays: true,
      onDropdownOptionsLoaded: async (allDropdownOptions, options) => {
        const documentCategoryFromMaster = allDropdownOptions?.["metadata/doc-masters"]?.find(dm => dm.id === data?.documentMasterId);
        if (data?.documentMasterId && documentCategoryFromMaster) {
          options.setInputs({
            ...options.inputs,
            documentCategory: documentCategoryFromMaster?.documentCategoryName || "",
          });
        }

        await getApplicationAndPropertiesOptions(options, fetchData);
      }
    }));
  }, [data]);

  useEffect(() => {
    if (data?.applicationLoanIds) {
      const options = {
        lastUpdated: "applicationLoanIds",
        inputs: { applicationLoanIds: data?.applicationLoanIds }
      };

      onDocumentFormChanged(options, fetchData).catch(e => {
        if (FeatureFlags.IS_DEBUG) {
          console.log(e);
        }
      });
    }
  }, [data]);

  return <LmsFormGenerator
    myForm={myForm}
    isLoading={isLoading}
    error={error}
    actions={["save", "save-close", "delete", "close"]}
    onFormChanged={(options) => onDocumentFormChanged(options, fetchData)}
  />;
};

export const DocumentsTable = () => {
  const actionsButtons = [
    "add",
    { text: "Edit", action: "edit", path: "documents" },
    "remove",
    "export",
    { text: "Add Proforma", action: "add", path: "documents/addproforma" }
  ];

  const history = useHistory();
  const handleDocumentsTableClick = async (
    rowData,
    _j?: number,
    prop?: string
  ) => {
    const { documentFileName, id } = rowData.clickedRow;
    if ("documentFileName" === prop && documentFileName && id) {
      await downloadFile(
        "document/download/{id}",
        {
          fileName: documentFileName,
          id
        }
      );
    }

    if ("documentMaster" === prop && id) {
      history.push(`documents/${id}`);
    }
  };

  return <LmsTable
    actionButtons={actionsButtons}
    useApiFiltering
    apiSuffix="document/logs"
    deleteSuffix="document"
    rowsBackgroundCustomColor={(row) => row.expiryDateColorId}
    columnLabelsDictionary={{
      includeInLrcpolling: "Include In LRC",
      expiryDate: "Expiration Date",
      isArchived: "Archive"
    }}
    onClickDownloadPath="draws/export-histories?loanId={id}"
    onClickIdProp="loanId"
    columnClickOptions={{
      documentFileName: { downloadPath: "document/download", idProp: "documentMasterId" },
      documentMaster: { path: "view", idProp: "id" }
    }}
    handleClick={handleDocumentsTableClick}
  />;
};

const LmsDocuments = ({ ...props }) => {
  const { params = {}, history } = props;
  const allowedActions = ["add"];
  const allowedSubpages = ["addproforma"] as any[];
  const { isAdd, isEdit, actionLower, isMainPage, isSubpageAllowed, isActionAllowed, id: documentId, query } = useParseRouteActions(params, allowedActions, allowedSubpages);
  const loanDetailsId = query.get("loanDetailsId") || "";
  const loanId = query.get("loanId") || "";
  const nwccmessageId = query.get("nwccmessageId") || "";

  if (!isActionAllowed) {
    history.goBack();
  }

  return <React.Fragment>
    {isMainPage && <DocumentsTable />}
    {isAdd && <AddDocumentForm loanDetailsId={loanDetailsId} loanId={loanId} nwccmessageId={nwccmessageId} />}
    {isEdit && <EditDocumentForm documentId={documentId} />}
    {isSubpageAllowed && <React.Fragment>
      {actionLower === "addproforma" && <AddProformaForm loanId={loanId} />}
    </React.Fragment>}
  </React.Fragment>;
};

export default LmsDocuments;